/**
 * Local Storage Utils
 * 
 * This module handles saving and retrieving game state from localStorage.
 * It supports multiple game types (Weaver, Weaver X, etc) while maintaining
 * backwards compatibility with the original Weaver game.
 * 
 * Storage Format:
 * - Original Weaver (backwards compatibility):
 *   - startWord: string
 *   - endWord: string
 *   - enteredWords: string[]
 *   - isGameOver: boolean
 * 
 * - All Game Types (including Weaver):
 *   - startWord_<gameType>: string
 *   - endWord_<gameType>: string
 *   - enteredWords_<gameType>: string[]
 *   - isGameOver_<gameType>: boolean
 * 
 * Migration Strategy:
 * - For GameType.WEAVER: Read from new format first, fall back to old format and migrate
 * - For GameType.WEAVER: Write to both new and old formats
 * - For other game types: Use new format only
 */

import { getGameConfig } from '../types/GameConfig.ts';
import { GameType } from '../types/GameType.ts';

export function setStartWordLocalStorage(gameType: GameType, gameNumber: number, startWord: string): void {
  const gameConfig = getGameConfig(gameType);
  if (gameConfig.isGameNumberCurrentGame(gameNumber)) {
    // Always write to new format
    localStorage.setItem(`startWord_${gameType}`, startWord);

    // Also write to old format for Weaver (backwards compatibility)
    if (gameType === GameType.WEAVER) {
      localStorage.setItem('startWord', startWord);
    }
  }
}

export function getStartWordLocalStorage(gameType: GameType, gameNumber: number): string | null {
  const gameConfig = getGameConfig(gameType);
  if (gameConfig.isGameNumberCurrentGame(gameNumber)) {
    if (gameType === GameType.WEAVER) {
      // Try new format first
      const newFormat = localStorage.getItem(`startWord_${gameType}`);
      if (newFormat !== null) {
        return newFormat;
      }
      // Fall back to old format and migrate
      const oldFormat = localStorage.getItem('startWord');
      if (oldFormat !== null) {
        // Migrate to new format
        localStorage.setItem(`startWord_${gameType}`, oldFormat);
        return oldFormat;
      }
      return null;
    }
    return localStorage.getItem(`startWord_${gameType}`);
  }
  return null;
}

export function setEndWordLocalStorage(gameType: GameType, gameNumber: number, endWord: string): void {
  const gameConfig = getGameConfig(gameType);
  if (gameConfig.isGameNumberCurrentGame(gameNumber)) {
    // Always write to new format
    localStorage.setItem(`endWord_${gameType}`, endWord);

    // Also write to old format for Weaver (backwards compatibility)
    if (gameType === GameType.WEAVER) {
      localStorage.setItem('endWord', endWord);
    }
  }
}

export function getEndWordLocalStorage(gameType: GameType, gameNumber: number): string | null {
  const gameConfig = getGameConfig(gameType);
  if (gameConfig.isGameNumberCurrentGame(gameNumber)) {
    if (gameType === GameType.WEAVER) {
      // Try new format first
      const newFormat = localStorage.getItem(`endWord_${gameType}`);
      if (newFormat !== null) {
        return newFormat;
      }
      // Fall back to old format and migrate
      const oldFormat = localStorage.getItem('endWord');
      if (oldFormat !== null) {
        // Migrate to new format
        localStorage.setItem(`endWord_${gameType}`, oldFormat);
        return oldFormat;
      }
      return null;
    }
    return localStorage.getItem(`endWord_${gameType}`);
  }
  return null;
}

export function setEnteredWordsLocalStorage(gameType: GameType, gameNumber: number, enteredWords: string[]): void {
  const gameConfig = getGameConfig(gameType);
  if (gameConfig.isGameNumberCurrentGame(gameNumber)) {
    // Always write to new format
    localStorage.setItem(`enteredWords_${gameType}`, JSON.stringify(enteredWords));

    // Also write to old format for Weaver (backwards compatibility)
    if (gameType === GameType.WEAVER) {
      localStorage.setItem('enteredWords', JSON.stringify(enteredWords));
    }
  }
}

export function getEnteredWordsLocalStorage(gameType: GameType, gameNumber: number): string[] {
  const gameConfig = getGameConfig(gameType);
  if (gameConfig.isGameNumberCurrentGame(gameNumber)) {
    if (gameType === GameType.WEAVER) {
      // Try new format first
      const newFormat = localStorage.getItem(`enteredWords_${gameType}`);
      if (newFormat !== null) {
        try {
          return JSON.parse(newFormat);
        } catch (error) {
          // If new format has invalid JSON, return empty array
          return [];
        }
      }
      // Fall back to old format and migrate
      const oldFormat = localStorage.getItem('enteredWords');
      if (oldFormat !== null) {
        try {
          const parsed = JSON.parse(oldFormat);
          // Migrate to new format
          localStorage.setItem(`enteredWords_${gameType}`, oldFormat);
          return parsed;
        } catch (error) {
          // If old format has invalid JSON, return empty array
          return [];
        }
      }
      return [];
    }
    const stored = localStorage.getItem(`enteredWords_${gameType}`);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (error) {
        // If stored data has invalid JSON, return empty array
        return [];
      }
    }
    return [];
  }
  return [];
}

export function setIsGameOverLocalStorage(gameType: GameType, gameNumber: number, isGameOver: boolean): void {
  const gameConfig = getGameConfig(gameType);
  if (gameConfig.isGameNumberCurrentGame(gameNumber)) {
    // Always write to new format
    localStorage.setItem(`isGameOver_${gameType}`, String(isGameOver));

    // Also write to old format for Weaver (backwards compatibility)
    if (gameType === GameType.WEAVER) {
      localStorage.setItem('isGameOver', String(isGameOver));
    }
  }
}

export function getIsGameOverLocalStorage(gameType: GameType, gameNumber: number): boolean {
  const gameConfig = getGameConfig(gameType);
  if (gameConfig.isGameNumberCurrentGame(gameNumber)) {
    if (gameType === GameType.WEAVER) {
      // Try new format first
      const newFormat = localStorage.getItem(`isGameOver_${gameType}`);
      if (newFormat !== null) {
        return newFormat === 'true';
      }
      // Fall back to old format and migrate
      const oldFormat = localStorage.getItem('isGameOver');
      if (oldFormat !== null) {
        // Migrate to new format
        localStorage.setItem(`isGameOver_${gameType}`, oldFormat);
        return oldFormat === 'true';
      }
      return false;
    }
    return localStorage.getItem(`isGameOver_${gameType}`) === 'true';
  }
  return false;
}